import React, { useContext } from 'react';

import DashboardLayout from '../layouts/Dashboard/Dashboard';

import NewAccounts from '../components/NewAccount/NewAccounts';
import NewAccountExplore from '../components/NewAccount/NewAccountExplorer';
import { notifications } from '../components/Dashboard/testData';
import CustomerDataProvider, { customerDataContext } from '../contexts/CustomerDataProvider';
import { setPageTitle } from '../utils/helpers';
import envConfig from '../config/env-config';

import downloadIcon from '../assets/images/download.svg';
import downloadIconNarrow from '../assets/images/download-narrow.svg';

import {
    productKeys,
    onlineFlexiSaverUrlSegment,
    noticeSaverUrlSegment,
    mapProductTypeFromUrl, productTypes, productCodeToUrlSegment
} from '../utils/productHelpers';
import Button from "../components/Form/Button/Button";
import {useUnifiedAnalytics} from "../contexts/AnalyticsWrapper";
import IsaTransferPage from '../components/ProductPage/IsaTransferPage/IsaTransferPage';
import withFeatureFlags from '../components/FeatureFlags/withFeatureFlags';

/** @import { Product } from '../contexts/CustomerDataProviderTypes' */
/** @import { ComponentDataOfMapper } from './NewAccountPageContainerTypes' */
/** @import {WithFeatureFlagsProps} from '../components/FeatureFlags/FeatureFlagsTypes' */

const isUrlAbsolute = (/** @type {string} */ url) => {
    let r = new RegExp('^(?:[a-z]+:)?//', 'i');
    return (r.test(url));
}

const ApplicationsDisabledNotice = ({ className = '' }) => (
    <div className={`funding-source-selection__notice ${className}`.trim()}>
        <div className="funding-source-selection__info-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12.8104 1.66837C12.2931 1.43834 11.7025 1.43834 11.1852 1.66837C10.7856 1.84602 10.5261 2.15894 10.3455 2.41391C10.1679 2.66461 9.97784 2.99303 9.76877 3.35422L1.50161 17.6339C1.2917 17.9964 1.10095 18.3258 0.971609 18.6054C0.840159 18.8895 0.697554 19.2714 0.743003 19.7074C0.801814 20.2715 1.09736 20.7841 1.55607 21.1176C1.91057 21.3753 2.31256 21.4433 2.62432 21.4719C2.93107 21.5 3.31173 21.5 3.73061 21.5H20.2649C20.6838 21.5 21.0645 21.5 21.3712 21.4719C21.683 21.4433 22.085 21.3753 22.4395 21.1176C22.8982 20.7841 23.1937 20.2715 23.2525 19.7074C23.298 19.2714 23.1554 18.8895 23.0239 18.6054C22.8946 18.3258 22.7039 17.9964 22.494 17.6339L14.2268 3.35419C14.0177 2.99301 13.8276 2.66459 13.65 2.41391C13.4694 2.15894 13.2099 1.84602 12.8104 1.66837ZM12.9978 9V13C12.9978 13.5523 12.5501 14 11.9978 14C11.4455 14 10.9978 13.5523 10.9978 13V9C10.9978 8.44772 11.4455 8 11.9978 8C12.5501 8 12.9978 8.44772 12.9978 9ZM11.9978 16H12.0078C12.5601 16 13.0078 16.4477 13.0078 17C13.0078 17.5523 12.5601 18 12.0078 18H11.9978C11.4455 18 10.9978 17.5523 10.9978 17C10.9978 16.4477 11.4455 16 11.9978 16Z" fill="#BE732F"/>
            </svg>
        </div>
        <div className="funding-source-selection__info-content">
            <div className="funding-source-selection__notice-body">We're temporarily pausing new applications for this account. We'll be opening them again soon.</div>
        </div>
    </div>
);

const NewAccountPageContainer = (/** @type {WithFeatureFlagsProps} */ { featureEnabled }) => {

    const customerData = useContext(customerDataContext);
    const analytics = useUnifiedAnalytics();

    // ISA Limit logic - same as in NewAccounts.jsx
    const ISA_ALLOWANCE_BLOCK_THRESHOLD = 19000.01;
    const isaUsed = customerData?.data?.customer?.isaAllowance?.usedAllowance ?? 0;

    const ISA_Limit_Breach = isaUsed >= ISA_ALLOWANCE_BLOCK_THRESHOLD;

    const isSecondaryApplicationsDisabled = (/** @type {string} */ productType) => {
        if (featureEnabled('SecondaryAccountAppsOffline')) {
            return true;
        }

        return (productType === productTypes.EasyAccess && featureEnabled('SecondaryAccountOFSDisabled'))
            || (productType === productTypes.Notice && featureEnabled('SecondaryAccountNSDisabled'))
            || (productType === productTypes.FixedTerm && featureEnabled('SecondaryAccountFRSDisabled'))
            || (productType === productTypes.FixedRateCashISA && featureEnabled('SecondaryAccountFISADisabled'));
    };

    const openDocument = (/** @type {string} */ file) => (/** @type {React.MouseEvent<HTMLElement>} */ e) => {
        e.preventDefault();

        const url = isUrlAbsolute(file) ? file : `${envConfig.links.documentUrl}${file}`;

        analytics.trackAnalyticsDownloadEvent(
            'download file',
            { url: url, region: 'important info', type: 'link',action: 'download',
                productInfo: {
                    productName: '',
                    id: ''
                }
            },
            { fileName: file, fileDescription: file },
            `NewAccountPage|download_click|${file}`
        );


        window.open(url, "_blank");
    };

    /** @type {ComponentDataOfMapper} */
    const mapComponentDataOfs = (products) => {
        const product = products[0];
        const url = `${envConfig.links.applyFormLink}/existing-customer/apply?p=${onlineFlexiSaverUrlSegment()}`;

        const handleApplyClick = () => {
            const productName = 'ofs';

            analytics.trackAnalyticsClickEvent(
                `apply for an ${productName.toLowerCase()}`,
                url,
                '',
                productName,
                '',
                {},
                'link click',
                `Investec-Save-secure|page_load|new-account-${productName.toLowerCase()}`,
                'secondary application'
            );
        }

        return {
            DisplayName: product.name,
            Code: productKeys.OnlineFlexiSaver,
            Apply: url,
            ApplyButtonLabel: 'Apply for an Online Flexi Saver',
            ApplyOnClick: handleApplyClick,
            Summary: <>
                <p>The Online Flexi Saver is a simple and secure savings account, offering instant access to your savings and unlimited deposits and withdrawals.</p>
                <p>&nbsp;</p>
                <p>There are no hidden fees or charges, and you can set your monthly interest to be paid into your linked current account or grow your savings quicker by adding it back into your Online Flexi Saver.</p>
            </>,
            HowThisWorks: <>
                <p><b>How does this account work?</b></p>
                <p>You can move money to and from your linked account and your Online Flexi Saver as often as you like, with instant access to your balance and no withdrawal fees. Any interest you earn will be paid monthly. You won’t be able to use any other accounts to move money to or from.</p>
                <p>&nbsp;</p>
                <p><b>Your linked account</b></p>
                <p>This is the account you use to move money into and out of your Investec Save accounts.</p>
                <p>&nbsp;</p>
                <p>Your existing linked account will also apply to your new Online Flexi Saver.</p>
            </>,
            ImportantInformation: <>
                <p><b>Important information</b></p>
                <p>Please download and read the following documents:</p>
                <ul className='newaccount-explorer__links'>
                    <li>
                        <a href='#' target="_blank" onClick={openDocument(product.summaryBoxFileName)} data-analytics-itemclicked="Summary Box Bottom">
                            <img className='newaccount-explorer__download-icon' src={downloadIcon} />
                            <img className='newaccount-explorer__download-icon--desktop' src={downloadIconNarrow} />Summary box</a>
                    </li>
                    <li>
                        <a href='#' target="_blank" onClick={openDocument(window.raeEnv?.generalTermsFileName || '')}>
                            <img className='newaccount-explorer__download-icon' src={downloadIcon} />
                            <img className='newaccount-explorer__download-icon--desktop' src={downloadIconNarrow} />General terms</a>
                    </li>
                    <li>
                        <a href='#' target="_blank" onClick={openDocument(product.productTermsFileName)}>
                            <img className='newaccount-explorer__download-icon' src={downloadIcon} />
                            <img className='newaccount-explorer__download-icon--desktop' src={downloadIconNarrow} />
                            Product terms - {product.name}
                        </a>
                    </li>
                    <li>
                        <a href="#" target="_blank" className="fscs-link" onClick={openDocument('https://www.investec.com/content/dam/united-kingdom/downloads-and-documents/private-banking/FSCS-Information-and-Exclusions.pdf')}>
                            <img className='newaccount-explorer__download-icon' src={downloadIcon} />
                            <img className='newaccount-explorer__download-icon--desktop' src={downloadIconNarrow} />
                            <span>FSCS information sheet</span>
                        </a>
                    </li>
                    <li className="no-padding">
                        <a href="#" target="_blank" onClick={openDocument('https://www.investec.com/en_gb/legal/UK/data-protection-notice/private-bank.html')}>
                            <img className='newaccount-explorer__download-icon' src={downloadIcon} />
                            <img className='newaccount-explorer__download-icon--desktop' src={downloadIconNarrow} />
                            <span>Data Protection Notice</span>
                        </a>
                    </li>
                </ul>
            </>
        }
    };

    /** @type {ComponentDataOfMapper} */
    const mapComponentDataNs = (/** @type {Product[]} */ products) => {
        const product = products[0];
        const url = `${envConfig.links.applyFormLink}/existing-customer/apply?p=${noticeSaverUrlSegment()}`;
        const handleApplyClick = () => {
            const productName = 'ns';

            analytics.trackAnalyticsClickEvent(
                `apply for an ${productName.toLowerCase()}`,
                url,
                '',
                productName,
                '',
                {},
                'link click',
                `Investec-Save-secure|page_load|new-account-${productName.toLowerCase()}`,
                'secondary application'
            );
            window.location.href = url;
        };

    return {
                DisplayName: `${product.name} Issue ${product.issueNumber}`,
                Code: productKeys.NoticeSaver,
                Apply: url,
                ApplyButtonLabel: 'Apply for a 90-Day Notice Saver',
                ApplyOnClick: handleApplyClick,
                Summary: <>
                    <p>Offering a higher rate than our instant access account and the flexibility to access your savings in 90 days, this account gives you the peace of mind that comes with knowing your money can be made available if you need it.</p>
                </>,
                HowThisWorks: <>
                    <p><b>Your notice period</b></p>
                    <p>All withdrawals are subject to a 90-day notice period, which will start on the calendar day that you tell us you’d like to make a withdrawal. You’ll still earn interest during this period.</p>
                    <p>&nbsp;</p>
                    <p>You can cancel a withdrawal at any time up until 20:00 the evening before your notice period ends.</p>
                    <p>&nbsp;</p>
                    <p><b>Your linked account</b></p>
                    <p>This is the account you use to move money into your Investec Save accounts.</p>
                    <p>&nbsp;</p>
                    <p>Your existing linked account will also apply to your new 90-Day Notice Saver.</p>
                </>,
                ImportantInformation: <>
                    <p><b>Important information</b></p>
                    <p>Please download and read the following documents:</p>
                    <ul className='newaccount-explorer__links'>
                        <li>
                            <a href='#' target="_blank" onClick={openDocument(product.summaryBoxFileName)} data-analytics-itemclicked="Summary Box Bottom">
                                <img className='newaccount-explorer__download-icon' src={downloadIcon} />
                                <img className='newaccount-explorer__download-icon--desktop' src={downloadIconNarrow} />Summary box</a>
                        </li>
                        <li>
                            <a href='#' target="_blank" onClick={openDocument(window.raeEnv?.generalTermsFileName || '')}>
                                <img className='newaccount-explorer__download-icon' src={downloadIcon} />
                                <img className='newaccount-explorer__download-icon--desktop' src={downloadIconNarrow} />General terms</a>
                        </li>
                        <li>
                            <a href='#' target="_blank" onClick={openDocument(product.productTermsFileName)}>
                                <img className='newaccount-explorer__download-icon' src={downloadIcon} />
                                <img className='newaccount-explorer__download-icon--desktop' src={downloadIconNarrow} />
                                Product terms - {product.name}
                            </a>
                        </li>
                        <li>
                            <a href="#" target="_blank" className="fscs-link" onClick={openDocument('https://www.investec.com/content/dam/united-kingdom/downloads-and-documents/private-banking/FSCS-Information-and-Exclusions.pdf')}>
                                <img className='newaccount-explorer__download-icon' src={downloadIcon} />
                                <img className='newaccount-explorer__download-icon--desktop' src={downloadIconNarrow} />
                                <span>FSCS information sheet</span>
                            </a>
                        </li>
                        <li className="no-padding">
                            <a href="#" target="_blank" onClick={openDocument('https://www.investec.com/en_gb/legal/UK/data-protection-notice/private-bank.html')}>
                                <img className='newaccount-explorer__download-icon' src={downloadIcon} />
                                <img className='newaccount-explorer__download-icon--desktop' src={downloadIconNarrow} />
                                <span>Data Protection Notice</span>
                            </a>
                        </li>
                    </ul>
                </>
    };
    };


    /** @type {ComponentDataOfMapper} */
    const mapComponentDataFrs = (/** @type {Product[]} */ products) => {
        var product = products[0]
        var multipleProductsAvailable = products.length > 1
        const isApplyDisabled = isSecondaryApplicationsDisabled(productTypes.FixedTerm);
        return {
            DisplayName: multipleProductsAvailable ? 'Fixed Rate Saver' : product.name,
            Code: productKeys.FixedRateSaver,
            Apply: `${envConfig.links.applyFormLink}/existing-customer/apply?p=${productCodeToUrlSegment(product.code)}`,
            ApplyButtonLabel: multipleProductsAvailable ? null : `Apply for a ${product.name}`,
            Summary: <>{multipleProductsAvailable ? <>
                    <p>The Fixed Rate Saver is an Investec Save account that currently offers a fixed term for a fixed period of time. The interest rate will vary depending on the term you choose.
                        Make as many deposits as you like for the first seven days up to £250,000, then we’ll take care of the rest.</p>
                    <div className={'term-section'}>
                        {isApplyDisabled && <ApplicationsDisabledNotice className="funding-source-selection__notice--above-list" />}
                        {products.map(p =>
                            <ProductTermCard product={p} key={p.code} isApplyDisabled={isApplyDisabled} showDisabledNotice={false} />)}
                    </div>
                </> :
                <>
                    <p>The Fixed Rate Saver is an Investec Save account that offers a {product.rate}% interest rate,
                        with a one-year fixed term.</p>
                    <p>&nbsp;</p>
                    <p>Make as many deposits as you like for the first seven days up to £250,000, then we’ll take care
                        of the rest. You won’t be able to access your savings until we pay your balance and interest
                        into your linked account automatically at the end of your fixed term.</p>
                </>}</>,
            HowThisWorks: <>
                <p><b>How does this account work?</b></p>
                <p>&nbsp;</p>
                <p><b>Your funding window</b></p>
                <p>Once your account is open, you'll have seven calendar days to add £5,000-£250,000. You can make
                    unlimited deposits during this period. If you don't add at least £5,000 by the end of the seven
                    days, we'll return any money and interest earnings and close your account automatically.</p>
                <p>&nbsp;</p>
                <p>You won't be able to make any withdrawals from this account for the full duration of your fixed
                    term, including the seven-day funding window.</p>
                <p>&nbsp;</p>
                <p><b>Your linked account</b></p>
                <p>This is the account you use to move money into your Investec Save accounts, and it's where your
                    final balance will be returned to at the end of your term.</p>
                <p>&nbsp;</p>
                <p>Your existing linked account will also apply to your new Fixed Rate Saver.</p>
            </>,
            ImportantInformation: <>
                <p><b>Important information</b></p>
                <p>Please download and read the following documents:</p>
                <ul className='newaccount-explorer__links'>
                    <li>
                        <a href='#' target="_blank" onClick={openDocument(product.summaryBoxFileName)} data-analytics-itemclicked="Summary Box Bottom">
                            <img className='newaccount-explorer__download-icon' src={downloadIcon}/>
                            <img className='newaccount-explorer__download-icon--desktop' src={downloadIconNarrow}/>Summary
                            box</a>
                    </li>
                    <li>
                        <a href='#' target="_blank" onClick={openDocument(window.raeEnv?.generalTermsFileName || '')}>
                            <img className='newaccount-explorer__download-icon' src={downloadIcon}/>
                            <img className='newaccount-explorer__download-icon--desktop' src={downloadIconNarrow}/>General
                            terms</a>
                    </li>
                    <li>
                        <a href='#' target="_blank" onClick={openDocument(product.productTermsFileName)}>
                            <img className='newaccount-explorer__download-icon' src={downloadIcon}/>
                            <img className='newaccount-explorer__download-icon--desktop' src={downloadIconNarrow}/>
                            Product terms - Fixed Rate Saver
                        </a>
                    </li>
                    <li>
                        <a href="#" target="_blank" className="fscs-link" onClick={openDocument('https://www.investec.com/content/dam/united-kingdom/downloads-and-documents/private-banking/FSCS-Information-and-Exclusions.pdf')}>
                            <img className='newaccount-explorer__download-icon' src={downloadIcon}/>
                            <img className='newaccount-explorer__download-icon--desktop' src={downloadIconNarrow}/>
                            <span>FSCS information sheet</span>
                        </a>
                    </li>
                    <li className="no-padding">
                        <a href="#" target="_blank" onClick={openDocument('https://www.investec.com/en_gb/legal/UK/data-protection-notice/private-bank.html')}>
                            <img className='newaccount-explorer__download-icon' src={downloadIcon}/>
                            <img className='newaccount-explorer__download-icon--desktop' src={downloadIconNarrow}/>
                            <span>Data Protection Notice</span>
                        </a>
                    </li>
                </ul>
            </>
        }
    }

    // --- FISA product mapping (based on ISA limits) ---
    /**
     * FISA product availability is controlled by ISA limit breach status.
     * When ISA_Limit_Breach = true, FISA product is disabled due to ISA allowance limits.
     * When ISA_Limit_Breach = false, FISA product is available (subject to feature flag).
     */
    let mapComponentDataFisa = null;
    if (!ISA_Limit_Breach) {
        mapComponentDataFisa = (/** @type {Product[]} */ products) => {
            var product = products[0]
            const url = `${envConfig.links.applyFormLink}/existing-customer/apply?p=${productCodeToUrlSegment(product.code)}`;
            
            const handleApplyClick = () => {
                const productName = 'fisa';
                analytics.trackAnalyticsClickEvent(
                    `apply for an ${productName.toLowerCase()}`,
                    url,
                    '',
                    productName,
                    '',
                    {},
                    'link click',
                    `Investec-Save-secure|page_load|new-account-${productName.toLowerCase()}`,
                    'secondary application'
                );
            };

            return {
                DisplayName: 'Fixed Rate Cash ISA',
                Code: productKeys.FixedRateCashISA,
                Apply: url,
                ApplyButtonLabel: null,
                ApplyOnClick: handleApplyClick,
                Summary: <>
                    <p>If you want to make your savings work harder, consider our Fixed Rate Cash ISA. By depositing your money for a fixed term, you can
                        benefit from higher interest rates while enjoying tax-free returns.</p>
                    <div className={'term-section'}>
                        {products.map(p =>
                            <ProductTermCard product={p} key={p.code} isApplyDisabled={featureEnabled && featureEnabled('SecondaryAccountFISADisabled')} />)}
                    </div>
                </>,
                HowThisWorks: <>
                    <p><b>How does this account work?</b></p>
                    <p>&nbsp;</p>
                    <p><b>Your funding window</b></p>
                    <p>Once your account is open, you'll have seven calendar days to add £1,000-£20,000. You can make unlimited deposits during this period.</p>
                    <p>&nbsp;</p>
                    <p>If your balance doesn’t reach £1,000 by the end of day seven, we’ll return any money (plus interest) to your linked account and automatically close the account.</p>
                    <p>&nbsp;</p>
                    <p>You can withdraw your funds at any point during the 14-day cancellation period without paying an early closure fee. This won't affect your ISA allowance. After 14 days, you can still close your account, but an early closure fee may apply and your ISA allowance could be affected.</p>
                    <p>&nbsp;</p>
                    <p><b>Your linked account</b></p>
                    <p>This is the account you use to move money into your Investec Save accounts. Your existing
                        linked account will also apply to your new Fixed Rate Cash ISA.
                    </p>
                </>,
                ImportantInformation: <>
                    <p><b>Important information</b></p>
                    <p>Please download and read the following documents:</p>
                    <ul className='newaccount-explorer__links'>
                        <li>
                            <a href='#' target="_blank" onClick={openDocument(product.summaryBoxFileName)} data-analytics-itemclicked="Summary Box Bottom">
                                <img className='newaccount-explorer__download-icon' src={downloadIcon} />
                                <img className='newaccount-explorer__download-icon--desktop' src={downloadIconNarrow} />Summary box & key features</a>
                        </li>
                        <li>
                            <a href='#' target="_blank" onClick={openDocument(window.raeEnv?.generalTermsFileName || '')}>
                                <img className='newaccount-explorer__download-icon' src={downloadIcon} />
                                <img className='newaccount-explorer__download-icon--desktop' src={downloadIconNarrow} />General terms</a>
                        </li>
                        <li>
                            <a href='#' target="_blank" onClick={openDocument(product.productTermsFileName)}>
                                <img className='newaccount-explorer__download-icon' src={downloadIcon} />
                                <img className='newaccount-explorer__download-icon--desktop' src={downloadIconNarrow} />
                                Product terms - Fixed Rate Cash ISA
                            </a>
                        </li>
                        <li>
                            <a href="#" target="_blank" className="fscs-link" onClick={openDocument('https://www.investec.com/content/dam/united-kingdom/downloads-and-documents/private-banking/FSCS-Information-and-Exclusions.pdf')}>
                                <img className='newaccount-explorer__download-icon' src={downloadIcon} />
                                <img className='newaccount-explorer__download-icon--desktop' src={downloadIconNarrow} />
                                <span>FSCS information sheet</span>
                            </a>
                        </li>
                        <li className="no-padding">
                            <a href="#" target="_blank" onClick={openDocument('https://www.investec.com/en_gb/legal/UK/data-protection-notice/private-bank.html')}>
                                <img className='newaccount-explorer__download-icon' src={downloadIcon} />
                                <img className='newaccount-explorer__download-icon--desktop' src={downloadIconNarrow} />
                                <span>Data Protection Notice</span>
                            </a>
                        </li>
                    </ul>
                </>
            };
        };
    }


    const renderComponent = () => {
        const url = window.location.pathname;

        if (url === '/new-account/fisa/isa-transfer') {
            setPageTitle('Tax-free savings transfer your ISA to us | Investec Save');
            return <IsaTransferPage />;
        }

        if (mapProductTypeFromUrl(url) === productTypes.EasyAccess) {
            setPageTitle('New Online Flexi Saver Account | Investec Save');
            return <NewAccountExplore data={mapComponentDataOfs} />
        }

        if (mapProductTypeFromUrl(url) === productTypes.FixedTerm) {
            setPageTitle('New Fixed Rate Saver Account | Investec Save');
            return <NewAccountExplore data={mapComponentDataFrs} />
        }

        // --- FISA product route (based on ISA limits) ---
        if (!ISA_Limit_Breach && mapProductTypeFromUrl(url) === productTypes.FixedRateCashISA && mapComponentDataFisa) {
            setPageTitle('New Fixed Rate Cash ISA Account | Investec Save');
            return <NewAccountExplore data={mapComponentDataFisa} />
        }

        if (mapProductTypeFromUrl(url) === productTypes.Notice) {
            setPageTitle('New Notice Saver Account | Investec Save');
            return <NewAccountExplore data={mapComponentDataNs} />
        }

        setPageTitle('New Account | Investec Save');
        return <NewAccounts />
    };

    const isIsaTransferPage = window.location.pathname === '/new-account/fisa/isa-transfer';

    return (
        <CustomerDataProvider>
            <DashboardLayout notifications={notifications} className="my-profile-page" fullWidth={isIsaTransferPage}>
                {renderComponent()}
            </DashboardLayout>
        </CustomerDataProvider>
    );
};

const ProductTermCard = (/** @type {{product: Product, isApplyDisabled?: boolean, showDisabledNotice?: boolean}} */ {product, isApplyDisabled, showDisabledNotice = true}) => {

    const analytics = useUnifiedAnalytics();

    const goToApplicationForm = (/** @type {string} */ productCode, /** @type {string} */ _region, /** @type {string} */ productName) => {
        const url = `${envConfig.links.applyFormLink}/existing-customer/apply?p=${productCodeToUrlSegment(productCode)}`
        analytics.trackAnalyticsClickEvent(
            `apply for an ${productName.toLowerCase()}`,
            url,
            '',
            productCode,
            '',
            {},
            'link click',
            `Investec-Save-secure|page_load|new-account-${productName.toLowerCase()}`,
            'secondary application'
        );

        window.location.href = url;
    };

    return <div className={'term-card'} data-term-card-product-code={product.code}>
        <h3>{product.name}</h3>
        <div>
            <div className={'emphasis'}>
                <p><b>Annual interest</b></p>
            </div>
            <p><b>{product.rate}</b>% AER*/gross</p>
        </div>
        <Button size='med'
            label={'Apply now'}
            disabled={isApplyDisabled}
            onClick={!isApplyDisabled ? () => goToApplicationForm(product.code, 'frs term options', product.name) : undefined}
            dataAnalyticsItemClicked='Apply Now Top'
        />
        {isApplyDisabled && showDisabledNotice && (
            <div className="funding-source-selection__notice">
                <div className="funding-source-selection__info-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12.8104 1.66837C12.2931 1.43834 11.7025 1.43834 11.1852 1.66837C10.7856 1.84602 10.5261 2.15894 10.3455 2.41391C10.1679 2.66461 9.97784 2.99303 9.76877 3.35422L1.50161 17.6339C1.2917 17.9964 1.10095 18.3258 0.971609 18.6054C0.840159 18.8895 0.697554 19.2714 0.743003 19.7074C0.801814 20.2715 1.09736 20.7841 1.55607 21.1176C1.91057 21.3753 2.31256 21.4433 2.62432 21.4719C2.93107 21.5 3.31173 21.5 3.73061 21.5H20.2649C20.6838 21.5 21.0645 21.5 21.3712 21.4719C21.683 21.4433 22.085 21.3753 22.4395 21.1176C22.8982 20.7841 23.1937 20.2715 23.2525 19.7074C23.298 19.2714 23.1554 18.8895 23.0239 18.6054C22.8946 18.3258 22.7039 17.9964 22.494 17.6339L14.2268 3.35419C14.0177 2.99301 13.8276 2.66459 13.65 2.41391C13.4694 2.15894 13.2099 1.84602 12.8104 1.66837ZM12.9978 9V13C12.9978 13.5523 12.5501 14 11.9978 14C11.4455 14 10.9978 13.5523 10.9978 13V9C10.9978 8.44772 11.4455 8 11.9978 8C12.5501 8 12.9978 8.44772 12.9978 9ZM11.9978 16H12.0078C12.5601 16 13.0078 16.4477 13.0078 17C13.0078 17.5523 12.5601 18 12.0078 18H11.9978C11.4455 18 10.9978 17.5523 10.9978 17C10.9978 16.4477 11.4455 16 11.9978 16Z" fill="#BE732F"/>
                    </svg>
                </div>
                <div className="funding-source-selection__info-content">
                    <div className="funding-source-selection__notice-body">We're temporarily pausing new applications for this account. We'll be opening them again soon.</div>
                </div>
            </div>
        )}
    </div>
}

export default withFeatureFlags(NewAccountPageContainer);
